// Pure helpers behind the file-asset browser, kept out of the component so the
// formatting and ordering rules are unit-tested without rendering a grid.

const IMAGE_EXTENSIONS = ["apng", "avif", "bmp", "gif", "ico", "jpeg", "jpg", "png", "svg", "webp"];

// The server sends content_type and, for images, a presigned preview_url. Fall
// back to the extension so a listing from an older server still previews.
export function isImageFile(file) {
  if (!file) return false;
  const contentType = file.content_type || "";
  if (contentType) {
    return contentType.startsWith("image/") && contentType !== "image/tiff";
  }
  const name = file.name || "";
  const dot = name.lastIndexOf(".");
  if (dot < 0) return false;
  return IMAGE_EXTENSIONS.indexOf(name.slice(dot + 1).toLowerCase()) > -1;
}

// A file is only showable if the server signed a URL for it — an image whose
// presign failed still lists, just without a thumbnail.
export function previewUrl(file) {
  return isImageFile(file) && file.preview_url ? file.preview_url : null;
}

// thumbUrl is the small picture to show for an image: its thumbnail, or the
// image itself until the server has made one.
export function thumbUrl(file) {
  return (isImageFile(file) && file.thumb_url) || previewUrl(file);
}

// fileDimensions is an image's size: from the server once it has a
// thumbnail, otherwise as measured when the picture loaded.
export function fileDimensions(file, measured) {
  if (file && file.width && file.height) return { width: file.width, height: file.height };
  return measured;
}

const UNITS = ["B", "kB", "MB", "GB", "TB"];

// Decimal units (1 kB = 1000 B), the convention every OS file browser shows.
export function formatBytes(bytes) {
  if (bytes == null || isNaN(bytes)) return "";
  let size = Math.max(0, Number(bytes));
  let unit = 0;
  while (size >= 1000 && unit < UNITS.length - 1) {
    size /= 1000;
    unit += 1;
  }
  // Bytes are whole; anything scaled gets one decimal only while it's small
  // enough for that decimal to mean something.
  const decimals = unit === 0 ? 0 : size < 10 ? 1 : 0;
  return `${size.toFixed(decimals)} ${UNITS[unit]}`;
}

export function formatDimensions(dimensions) {
  if (!dimensions || !dimensions.width || !dimensions.height) return "";
  return `${dimensions.width} x ${dimensions.height}`;
}

export const SORT_OPTIONS = [
  { value: "newest", label: "Uploaded (new-old)" },
  { value: "oldest", label: "Uploaded (old-new)" },
  { value: "name", label: "Name (A-Z)" },
  { value: "largest", label: "Size (large-small)" },
];

export const DEFAULT_SORT = "newest";

function updatedAt(file) {
  const raw = file && file.updated_date;
  if (!raw) return 0;
  const ms = Date.parse(raw);
  return isNaN(ms) ? 0 : ms;
}

// Sorts a copy: the fetched list is state, and Array.sort is in place.
export function sortFiles(files, sort) {
  const rows = Array.isArray(files) ? files.slice() : [];
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
  switch (sort) {
    case "oldest":
      // Files predating stored timestamps sort as 0; name breaks those ties so
      // the order stays stable instead of depending on the listing.
      return rows.sort((a, b) => updatedAt(a) - updatedAt(b) || byName(a, b));
    case "name":
      return rows.sort(byName);
    case "largest":
      return rows.sort((a, b) => (b.size || 0) - (a.size || 0) || byName(a, b));
    case "newest":
    default:
      return rows.sort((a, b) => updatedAt(b) - updatedAt(a) || byName(a, b));
  }
}

// How many of a listing can be shown as pictures — drives the empty state in
// grid view ("you have files, none of them are images").
export function imageCount(files) {
  return (Array.isArray(files) ? files : []).filter(isImageFile).length;
}

// Tag ids on one file, as a plain array — the API sends tag objects.
export function tagIdsFor(file) {
  return ((file && file.tags) || []).map((t) => t.id);
}

export function hasTag(file, tagId) {
  return tagIdsFor(file).indexOf(tagId) > -1;
}

// Filtering happens in the browser: the listing isn't paginated, so the whole
// set is already here and a filter shouldn't cost a round trip.
export function filterByTag(files, tagId) {
  const rows = Array.isArray(files) ? files : [];
  if (!tagId) return rows;
  if (tagId === UNTAGGED) return rows.filter((f) => tagIdsFor(f).length === 0);
  return rows.filter((f) => hasTag(f, tagId));
}

// Sentinel for the "Untagged" choice in the filter — the one thing you can't
// express by picking a tag, and the one you need to find what still needs
// filing.
export const UNTAGGED = "__untagged__";

// Tags in use across the listing, with how many files carry each, so the filter
// can show counts and drop tags nothing uses.
export function tagUsage(files) {
  const counts = {};
  (Array.isArray(files) ? files : []).forEach((f) => {
    ((f && f.tags) || []).forEach((t) => {
      counts[t.id] = (counts[t.id] || 0) + 1;
    });
  });
  return counts;
}
